/**
 * @quotech/ui — Input
 *
 * Styled text input primitive. Wraps a native <input> with the shared
 * design tokens (border-border / bg-background / focus:ring-ring) and
 * forwards the ref + all native input attributes so it drops into any
 * form or controlled field unchanged.
 *
 * FLOW: props (native input attrs) → cn() class merge → <input>
 *
 * USAGE:
 *   import { Input } from '@quotech/ui'
 *   <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" />
 */
import { forwardRef, type InputHTMLAttributes } from 'react'
import { cn } from '../lib/utils'
import { inputDisplayClass } from '../lib/inputDisplay'

export type InputProps = InputHTMLAttributes<HTMLInputElement>

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      className={cn(
        inputDisplayClass(type),
        'h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm text-foreground shadow-sm transition-colors',
        'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  ),
)
Input.displayName = 'Input'
